import { supabase } from "@/integrations/supabase/client";
import { captureError } from "@/utils/captureError";

/**
 * Account-level persistence for a single `user_preferences` column, for the
 * preference contexts that mount ABOVE `UserProvider` in the tree.
 *
 * THE PROBLEM THIS SOLVES. `UserContext.logout` performs a full
 * `localStorage.clear()` whenever biometric is not enrolled — deliberate and
 * documented (MOBILE_BUILD.md), and NOT to be removed. Combined with the idle
 * auto-logout (15 min admin / 30 min user, also deliberate), any preference
 * that lives only in `localStorage` is silently destroyed on a timer. The fix
 * is to make the account the source of truth so the value comes back on the
 * next sign-in, on any device.
 *
 * WHY NOT `useUserPreferences`. `FontSizeProvider` and `SimpleModeProvider` sit
 * outside `UserProvider` in `App.tsx`, so they cannot call `useUser()`. This
 * reads the session straight from supabase-auth instead — the same approach
 * `LanguageContext.refreshAccountLanguage` already uses for `ui_language`, and
 * for the same reason.
 *
 * ORDER OF PRECEDENCE, which matters for perceived speed:
 *   1. `localStorage` is read synchronously at mount, so the user's own last
 *      choice paints immediately with no flash of the default.
 *   2. The account value arrives a moment later and wins if it differs. On a
 *      fresh device (or right after a logout wiped step 1) this is what
 *      restores the preference.
 *   3. Every change writes through to BOTH, so the two never diverge.
 *
 * Every operation is best-effort: a signed-out user, an offline device, or an
 * RLS refusal must leave the preference working locally rather than throwing
 * into a provider that wraps the whole app.
 */

/**
 * Outcome of an account read. The distinction matters: "the signed-in account
 * has no stored preference" must reset the on-screen value to the default,
 * whereas "we could not ask" must leave whatever is already showing alone.
 *
 * Without it a stale `localStorage` value was sticky forever — including one
 * left behind by a PREVIOUS user on a shared device, because
 * `UserContext.logout` deliberately preserves `localStorage` when biometric is
 * enrolled. That is how a user whose stored size is normal ends up looking at
 * "A++".
 */
export type PreferenceRead<T> =
  | { status: "value"; value: T }
  | { status: "absent" }        // signed in, row read fine, nothing stored
  | { status: "unavailable" };  // signed out, offline, RLS refusal, error

/** Read one preference column for the signed-in user. */
export async function loadAccountPreference<T>(
  column: string,
  isValid: (value: unknown) => value is T,
): Promise<PreferenceRead<T>> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) return { status: "unavailable" };

    const { data, error } = await supabase
      .from("user_preferences")
      .select(column)
      .eq("user_id", session.user.id)
      .maybeSingle();

    if (error) return { status: "unavailable" };

    // `select()` with a runtime-chosen column name defeats supabase-js's
    // literal-type inference, so the row comes back as a union that includes
    // its error shape. Go through `unknown` rather than widening the caller.
    const value = (data as unknown as Record<string, unknown> | null)?.[column];
    return isValid(value) ? { status: "value", value } : { status: "absent" };
  } catch {
    return { status: "unavailable" };
  }
}


/**
 * Write one preference column for the signed-in user.
 *
 * Upsert on `user_id` rather than update, because a profile whose
 * `create_preferences_on_profile` row is missing for any reason would
 * otherwise silently match zero rows and report success.
 */
export async function saveAccountPreference(
  column: string,
  value: unknown,
): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) return;

    // THE RESULT OF THIS CALL USED TO BE DISCARDED, AND IT COST US (2026-09-07).
    // supabase-js reports a PostgREST rejection as `error`, it does not throw —
    // so a bare `await` with a `catch {}` around it cannot see a failure at all.
    // Every A- and A++ the user picked was answered
    //   400 {"code":"23514", ... violates check constraint
    //        "user_preferences_font_size_check"}
    // because the column's CHECK still listed the pre-rename `small`/`xlarge`
    // vocabulary (fixed in migration 20260907090000). The write never landed,
    // the on-screen value had already changed, and the next hydrate pulled the
    // stored value back — which is why it looked like the setting "reset itself".
    // Nothing anywhere logged a thing.
    //
    // Staying silent for the USER is still right: they have their preference on
    // this device via the local value and a sync failure is not their problem.
    // Staying silent for US was the defect. Anything that rejects a preference
    // write is a schema/RLS disagreement — a bug on our side, and the sort that
    // is invisible precisely until someone reports the symptom months later.
    const { error } = await supabase
      .from("user_preferences")
      .upsert(
        {
          user_id: session.user.id,
          [column]: value,
          updated_at: new Date().toISOString(),
        } as never,
        { onConflict: "user_id" },
      );

    if (error) {
      captureError("account-preference-save-failed", error, {
        column,
        // The value is a preference enum (a font size, a boolean), never PII.
        value: String(value),
        code: (error as { code?: string }).code,
      });
    }
  } catch (err) {
    captureError("account-preference-save-threw", err, { column, value: String(value) });
  }
}

/**
 * Subscribe to sign-in / sign-out so a provider can re-hydrate on the next
 * sign-in without polling. Returns an unsubscribe function.
 *
 * `onSignedIn` fires for SIGNED_IN and for INITIAL_SESSION (the page-refresh
 * path, where no SIGNED_IN event is emitted).
 */
export function onAuthPreferenceRefresh(
  onSignedIn: () => void,
  onSignedOut: () => void,
): () => void {
  const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
    if (event === "SIGNED_IN" || event === "INITIAL_SESSION") onSignedIn();
    else if (event === "SIGNED_OUT") onSignedOut();
  });
  return () => subscription.unsubscribe();
}
